import { useEffect, useState } from "react";
import { Scale, CalendarCheck, Database, CloudUpload, CheckCircle2, AlertTriangle, Store } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import { PosCard, RankTable } from "./PosTable";
import { fmtInt, fmtNumber } from "@/lib/format";
import { OPS } from "@/constants/testIds";

const PI = OPS.posInsights;

const MASTER_LABEL = {
    branches: { ar: "الفروع", en: "Branches" },
    stores: { ar: "المخازن", en: "Stores" },
    inventory_items: { ar: "أصناف المخزون", en: "Inventory items" },
    units: { ar: "الوحدات", en: "Units" },
    purchase_invoices: { ar: "فواتير الشراء", en: "Purchase invoices" },
};

function Stat({ label, value, tone }) {
    const { lang } = useI18n();
    return (
        <div className="rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-3">
            <p className="text-xs text-[color:var(--ink-muted)]">{label}</p>
            <p className="kpi-number text-xl font-extrabold tnum" style={{ color: tone || "var(--ink)" }}>
                <bdi dir="ltr">{value === null || value === undefined ? "\u2014" : fmtInt(value, lang)}</bdi>
            </p>
        </div>
    );
}

export default function PosDataPanel({ data }) {
    const { t, lang } = useI18n();
    const [masters, setMasters] = useState(null);
    const cov = data?.coverage || {};
    const rec = data?.reconciliation || {};

    useEffect(() => {
        let alive = true;
        api.get("/pos-insights/masters")
            .then((r) => { if (alive) setMasters(r.data); })
            .catch(() => { if (alive) setMasters(null); });
        return () => { alive = false; };
    }, []);

    const masterRows = Object.entries(masters?.masters || {}).map(([k, v]) => ({
        key: k,
        name: MASTER_LABEL[k] ? MASTER_LABEL[k][lang === "ar" ? "ar" : "en"] : k,
        rows: v.row_count,
        synced_at: (v.synced_at || "").slice(0, 16).replace("T", " "),
    }));

    return (
        <div className="grid grid-cols-1 gap-4">
            <PosCard icon={CalendarCheck} title={t("pi_coverage_title")} testId={PI.coverage}>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <Stat label={t("pi_coverage_days")} value={cov.days_with_data} tone="var(--success)" />
                    <Stat label={t("pi_coverage_missing")} value={cov.missing_days}
                        tone={cov.missing_days ? "var(--brand-orange)" : "var(--ink)"} />
                    <div className="rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-3">
                        <p className="text-xs text-[color:var(--ink-muted)]">{t("ss_cov_first")}</p>
                        <p className="text-sm font-semibold tnum text-[color:var(--ink)]"><bdi dir="ltr">{cov.first_day || "\u2014"}</bdi></p>
                    </div>
                    <div className="rounded-xl border border-[color:var(--border-soft)] surface-2 px-4 py-3">
                        <p className="text-xs text-[color:var(--ink-muted)]">{t("ss_cov_last")}</p>
                        <p className="text-sm font-semibold tnum text-[color:var(--ink)]"><bdi dir="ltr">{cov.last_day || "\u2014"}</bdi></p>
                    </div>
                </div>
            </PosCard>

            <PosCard icon={Scale} title={t("pi_recon_title")} desc={t("pi_recon_desc")} testId={PI.recon}
                right={
                    <Badge variant={rec.mismatches ? "warning" : "success"} className="gap-1">
                        {rec.mismatches ? <AlertTriangle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                        {rec.mismatches ? `${fmtInt(rec.mismatches, lang)}` : t("pi_recon_ok")}
                    </Badge>
                }>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    <Stat label={t("pi_recon_checked")} value={rec.checked} />
                    <Stat label={t("pi_recon_mismatch")} value={rec.mismatches}
                        tone={rec.mismatches ? "var(--danger)" : "var(--success)"} />
                    <Stat label={t("pi_archived")} value={rec.raw_archived_days} tone="var(--brand-brown)" />
                </div>
                {rec.worst?.length ? (
                    <div className="mt-3 space-y-1 text-xs text-[color:var(--ink-muted)]">
                        {rec.worst.slice(0, 8).map((w, i) => (
                            <p key={i}>
                                <bdi dir="ltr">{w.date}</bdi> · POS {w.pos_branch_id} · Δ{t("pi_kpi_in_store")}{" "}
                                <b className="tnum text-[color:var(--danger)]">
                                    <bdi dir="ltr">{fmtNumber(w.recon?.delta_net, lang, 2)}</bdi>
                                </b>{" "}
                                · Δ{t("pi_kpi_apps")}{" "}
                                <b className="tnum text-[color:var(--danger)]">
                                    <bdi dir="ltr">{fmtNumber(w.recon?.delta_ship, lang, 2)}</bdi>
                                </b>{" "}
                                · {fmtInt(w.recon?.delta_orders, lang)} {t("pi_col_orders")}
                            </p>
                        ))}
                    </div>
                ) : null}
            </PosCard>

            <PosCard icon={Store} title={t("pi_masters_title")} testId={PI.mastersCard}>
                <RankTable
                    testId={PI.mastersCard + "-table"}
                    rows={masterRows}
                    columns={[
                        { key: "name", label: t("pi_masters_title"), strong: true },
                        { key: "rows", label: t("pi_masters_rows"), type: "int" },
                        { key: "synced_at", label: t("ss_col_pulled_at") },
                    ]}
                    rowTestId={(r) => PI.masterRow(r.key)}
                />
            </PosCard>

            <PosCard icon={Database} title={t("pi_caps_title")} testId={PI.capabilities}>
                <div className="space-y-3">
                    <div>
                        <p className="mb-1.5 text-xs font-semibold text-[color:var(--success)]">{t("pi_caps_available")}</p>
                        <div className="flex flex-wrap gap-1.5">
                            {(masters?.capabilities?.available || []).map((c) => (
                                <Badge key={c} variant="secondary" className="text-[11px]">{c}</Badge>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="mb-1.5 text-xs font-semibold text-[color:var(--brand-orange)]">{t("pi_caps_missing")}</p>
                        <div className="flex flex-wrap gap-1.5">
                            {(masters?.capabilities?.not_exposed_by_pos || []).map((c) => (
                                <Badge key={c} variant="warning" className="text-[11px]">{c}</Badge>
                            ))}
                        </div>
                        <p className="mt-2 text-xs leading-relaxed text-[color:var(--ink-muted)]">
                            {lang === "ar" ? masters?.capabilities?.note_ar : masters?.capabilities?.note_en}
                        </p>
                    </div>
                    <p className="flex items-center gap-1.5 text-xs text-[color:var(--ink-muted)]">
                        <CloudUpload className="h-3.5 w-3.5" />
                        {t("pi_archived")}: <b className="tnum text-[color:var(--ink)]"><bdi dir="ltr">{fmtInt(rec.raw_archived_days || 0, lang)}</bdi></b>
                    </p>
                </div>
            </PosCard>
        </div>
    );
}
